import { KNOWN_NODE_PROPS, KNOWN_TABLE_ROW_PROPS } from './knownNodeProps';

export interface UnknownLayoutProp {
  /** Node type carrying the prop, or `table-row` for a row entry. */
  nodeType: string;
  prop: string;
}

/**
 * Props a layout sets that its node type does not define.
 *
 * A misspelt or invented prop is the quietest failure the DSL has: the layout
 * parses, covers every QID, renders, and drops the styling on the floor. The
 * author sees valid, the clinician sees an unstyled table, and nothing in
 * between says a word.
 *
 * Unknown *node types* are already caught elsewhere (the renderer cannot
 * dispatch them), so a node whose type is unrecognised is skipped rather than
 * reported prop by prop.
 */
export function collectLayoutUnknownProps(nodes: unknown): UnknownLayoutProp[] {
  const found = new Map<string, UnknownLayoutProp>();

  const record = (nodeType: string, prop: string) => {
    found.set(`${nodeType}.${prop}`, { nodeType, prop });
  };

  const walkRow = (row: unknown) => {
    if (row == null || typeof row !== 'object') return;
    for (const [key, value] of Object.entries(row)) {
      if (!KNOWN_TABLE_ROW_PROPS.includes(key)) record('table-row', key);
      // `label` holds nodes when it is not plain text, and a typo on one of
      // those is exactly as silent as a typo in a cell.
      if (key === 'cells' || key === 'label') walk(value);
    }
  };

  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      for (const child of node) walk(child);
      return;
    }
    if (node == null || typeof node !== 'object') return;

    const nodeType = (node as { type?: unknown }).type;
    const known =
      typeof nodeType === 'string' ? KNOWN_NODE_PROPS[nodeType] : undefined;

    for (const [key, value] of Object.entries(node)) {
      if (known && !known.includes(key)) record(nodeType as string, key);
      if (key === 'rows') {
        for (const row of Array.isArray(value) ? value : []) walkRow(row);
      } else if (value != null && typeof value === 'object') {
        walk(value);
      }
    }
  };

  walk(nodes);
  return [...found.values()].sort(
    (a, b) =>
      a.nodeType.localeCompare(b.nodeType) || a.prop.localeCompare(b.prop)
  );
}

export function formatUnknownLayoutPropSummary(
  issue: UnknownLayoutProp
): string {
  return `${issue.nodeType}.${issue.prop}`;
}
